/**
 * MailboxConnectionCard — wiederverwendbare Karte zum Verbinden/Trennen eines
 * geteilten @-Postfachs über Google OAuth (Gmail).
 *
 * Extrahiert aus EinstellungenPage, damit die Postfach-Neu-Verbindung auch als
 * eigenständige Seite (/mail-verbinden) nutzbar ist — ohne die riesige
 * EinstellungenPage.tsx anzufassen.
 *
 * Status:  POST /api/gmail-auth { action:'status', gmail_address }
 * Trennen: POST /api/gmail-auth { action:'disconnect', gmail_address }
 * Verbinden: Redirect zu accounts.google.com/o/oauth2/v2/auth (Rückkehr über
 *            /auth/google/callback), mit login_hint + postfach-spezifischem State.
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Link, Loader2, RefreshCw, Unlink, type LucideIcon } from 'lucide-react';

export type MailboxAccent = 'blue' | 'green';

interface MailboxConnectionCardProps {
  email: string;
  subtitle: string;
  /** Stabile Kennung für den OAuth-State (identifiziert das Postfach beim Callback/Token-Tausch). */
  oauthUserId: string;
  icon: LucideIcon;
  accent: MailboxAccent;
  roles: string[];
}

type MailboxStatus = 'checking' | 'connected' | 'expired' | 'disconnected' | 'error';

const MAILBOX_ACCENT: Record<MailboxAccent, { card: string; iconBox: string; icon: string; label: string; pill: string; tag: string; connect: string }> = {
  blue: {
    card: 'bg-blue-50 border-blue-100',
    iconBox: 'bg-blue-100',
    icon: 'text-blue-600',
    label: 'text-blue-600',
    pill: 'bg-blue-100 text-blue-700',
    tag: 'bg-white border-blue-200 text-blue-700',
    connect: 'bg-[#9B1C1C] hover:bg-[#7A1616]',
  },
  green: {
    card: 'bg-green-50 border-green-100',
    iconBox: 'bg-green-100',
    icon: 'text-green-600',
    label: 'text-green-600',
    pill: 'bg-green-100 text-green-700',
    tag: 'bg-white border-green-200 text-green-700',
    connect: 'bg-green-600 hover:bg-green-700',
  },
};

const GMAIL_OAUTH_SCOPES = [
  'https://www.googleapis.com/auth/gmail.modify',
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
].join(' ');

const GMAIL_OAUTH_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  '967597285093-v79e3dq5hlfi6d0q1fp1fbuk01dbns98.apps.googleusercontent.com';

/**
 * Startet den Google-OAuth-Flow gezielt für EIN Postfach.
 * Nutzt denselben Mechanismus wie die funktionierenden Postfächer
 * (redirect auf accounts.google.com, Rückkehr über /auth/google/callback),
 * setzt aber login_hint + einen postfach-spezifischen State, damit sich der
 * Nutzer als genau dieses Postfach anmeldet.
 */
function startMailboxOAuth(email: string, oauthUserId: string) {
  try {
    localStorage.setItem('gmail_oauth_state', oauthUserId);
    localStorage.setItem('gmail_return_path', '/einstellungen/integrationen');
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GMAIL_OAUTH_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', `${window.location.origin}/auth/google/callback`);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GMAIL_OAUTH_SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', `gmail_${oauthUserId}`);
    authUrl.searchParams.set('login_hint', email);
    window.location.href = authUrl.toString();
  } catch (err) {
    console.error('[Gmail] OAuth-Start fehlgeschlagen:', err);
    toast.error(`Verbindung zu ${email} konnte nicht gestartet werden.`);
  }
}

/**
 * Wiederverwendbare Karte für ein geteiltes @-Postfach.
 * Zeigt den ECHTEN Tokenstatus (aus /api/gmail-auth?action=status) und bietet
 * je nach Zustand einen funktionierenden Aktionsknopf:
 *  - verbunden:            "Neu verbinden" + "Trennen"
 *  - abgelaufen/getrennt:  "Verbinden" (bzw. "Neu verbinden" bei abgelaufen)
 * Kein statisches "Verbunden"-Etikett mehr ohne Aktion.
 */
export function MailboxConnectionCard({ email, subtitle, oauthUserId, icon: Icon, accent, roles }: MailboxConnectionCardProps) {
  const [status, setStatus] = useState<MailboxStatus>('checking');
  const [busy, setBusy] = useState(false);
  const theme = MAILBOX_ACCENT[accent];

  const checkStatus = useCallback(async () => {
    setStatus('checking');
    try {
      const res = await fetch('/api/gmail-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'status', gmail_address: email }),
      });
      const data = await res.json().catch(() => ({} as any));
      // Echter Tokenstatus: verbunden nur, wenn Token existiert und gültig ist.
      // needs_reconnect / expires_at in der Vergangenheit => abgelaufen.
      const expiresAt = data?.expires_at ? new Date(data.expires_at).getTime() : null;
      const isExpired =
        data?.needs_reconnect === true ||
        (typeof expiresAt === 'number' && !Number.isNaN(expiresAt) && expiresAt <= Date.now());
      if (data?.connected === true && !isExpired) {
        setStatus('connected');
      } else if (data?.connected === true && isExpired) {
        setStatus('expired');
      } else if (data?.needs_reconnect === true) {
        setStatus('expired');
      } else {
        setStatus('disconnected');
      }
    } catch (err) {
      console.error(`[Gmail] Statusprüfung für ${email} fehlgeschlagen:`, err);
      setStatus('error');
    }
  }, [email]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const handleConnect = () => startMailboxOAuth(email, oauthUserId);

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/gmail-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'disconnect', gmail_address: email }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${email} getrennt`);
      setStatus('disconnected');
    } catch (err) {
      console.error(`[Gmail] Trennen von ${email} fehlgeschlagen:`, err);
      toast.error(`${email} konnte nicht getrennt werden.`);
    } finally {
      setBusy(false);
      // Realen Zustand vom Server nachladen (falls Trennen serverseitig anders lief)
      checkStatus();
    }
  };

  const isConnected = status === 'connected';
  const isExpired = status === 'expired';

  return (
    <div className={`p-3 rounded-xl border ${theme.card}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${theme.iconBox}`}>
          <Icon className={`w-3.5 h-3.5 ${theme.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{email}</p>
          <p className={`text-[10px] font-medium ${theme.label}`}>{subtitle}</p>
        </div>

        {/* Statusanzeige + Aktionsknopf (echter Zustand, nie nur statisches Etikett) */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {status === 'checking' && (
            <span className="flex items-center gap-1 text-[9px] text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Prüfe...
            </span>
          )}

          {status === 'error' && (
            <button
              onClick={checkStatus}
              className="flex items-center gap-1 text-[9px] text-amber-600 hover:text-amber-700"
              title="Status konnte nicht geladen werden — erneut versuchen"
            >
              <AlertCircle className="w-3 h-3" />
              Erneut prüfen
            </button>
          )}

          {isConnected && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${theme.pill}`}>Verbunden</span>
          )}
          {isExpired && (
            <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Abgelaufen</span>
          )}

          {(isConnected || isExpired) && (
            <button
              onClick={handleConnect}
              disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors disabled:opacity-50"
              title="Als dieses Postfach bei Google neu anmelden"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Neu verbinden
            </button>
          )}

          {isConnected && (
            <button
              onClick={handleDisconnect}
              disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
              Trennen
            </button>
          )}

          {status === 'disconnected' && (
            <button
              onClick={handleConnect}
              disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors disabled:opacity-50 ${theme.connect}`}
            >
              <Link className="w-3.5 h-3.5" />
              Verbinden
            </button>
          )}
        </div>
      </div>

      {isExpired && (
        <p className="text-[10px] text-amber-600 mb-2">Zugriff abgelaufen — bitte neu verbinden, damit E-Mails wieder synchronisiert werden.</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {roles.map(role => (
          <span key={role} className={`text-[10px] border px-2 py-0.5 rounded-full ${theme.tag}`}>{role}</span>
        ))}
      </div>
    </div>
  );
}
